import React, { useState } from 'react';

import { now } from '@/utils/helpers/now';
import { startOfDay } from 'date-fns';

interface SelectedDateProps {
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
}

/**
 * Higher-Order Component that provides selected date state management.
 *
 * This HOC wraps a component and provides:
 * - selectedDate state initialized to today
 * - setSelectedDate function to update the date
 *
 * @param WrappedComponent - The component to wrap with date functionality
 * @returns Enhanced component with date props
 */
const withSelectedDate = <P extends SelectedDateProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const ComponentWithSelectedDate = (
    props: Omit<P, keyof SelectedDateProps>
  ) => {
    // Date state
    const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(now()));

    return (
      <WrappedComponent
        {...(props as P)}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    );
  };

  ComponentWithSelectedDate.displayName = `withSelectedDate(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithSelectedDate;
};

export default withSelectedDate;
